import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Typography, Result } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { PreJoin } from '@livekit/components-react';
import '@livekit/components-styles';
import { useGatheringIdentity } from '../../hooks/use_gathering_identity';
import VideoCallRoom from '../Hangouts/VideoCallRoom';
import CreditsBalanceTag from '../Hangouts/CreditsBalanceTag';
import PieceBackdrop from '../Skargard/PieceBackdrop';
import Fold from '../Skargard/Fold';
import '../Hangouts/VideoCall.css';

const { Title, Text } = Typography;

/**
 * The gathering's video rooms — the common room, and each published breakout
 * room — minted while the flow runs (pods/gatherings start_flow/publish_rooms;
 * see hangout_room_name / common_hangout_room_name). Unlike EntityCall this
 * isn't tied to an entity: just a bare LiveKit room name and a label, passed
 * as query params from wherever the join button lives (MemberView, FlowEditor,
 * RoomBoard). Works the same for a guest (useGatheringIdentity), whose
 * name is marked "(guest)" to everyone in the call.
 */
function GatheringCall() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { name, isGuest } = useGatheringIdentity();
    const roomName = params.get('room');
    const label = params.get('label') || 'Gathering';
    const participantIdentity = (isGuest ? `${name} (guest)` : name) || 'anonymous';

    const [userChoices, setUserChoices] = useState(null);
    const handleLeave = () => navigate('/gathering');

    if (!roomName) {
        return (
            <Result
                status="warning"
                title="No room to join"
                extra={<Button type="primary" onClick={handleLeave}>Back to Gathering</Button>}
            />
        );
    }

    if (userChoices) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-primary)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setUserChoices(null)}>
                        Back to waiting room
                    </Button>
                    <Text type="secondary" style={{ fontSize: 13 }}>{label}</Text>
                </div>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <VideoCallRoom
                        roomName={roomName}
                        participantIdentity={userChoices.username || participantIdentity}
                        entityId={null}
                        onLeave={handleLeave}
                        userChoices={userChoices}
                        fullscreen
                    />
                </div>
            </div>
        );
    }

    return (
        <PieceBackdrop piece={Fold}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleLeave}>
                        Go back
                    </Button>
                    {!isGuest && <div style={{ marginLeft: 'auto' }}><CreditsBalanceTag /></div>}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24, padding: 32 }}>
                    <div style={{ textAlign: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>{label}</Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>Set up your camera and microphone before joining</Text>
                    </div>
                    <div className="prejoin-wrapper" data-lk-theme="default">
                        <PreJoin
                            defaults={{ username: participantIdentity, videoEnabled: true, audioEnabled: true }}
                            onSubmit={setUserChoices}
                            onError={err => console.error('PreJoin error:', err)}
                            persistUserChoices
                        />
                    </div>
                </div>
            </div>
        </PieceBackdrop>
    );
}

export default GatheringCall;
